import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, ArrowRight, Calendar, CreditCard, MapPin, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/api/client";
import Reveal from "@/components/Reveal";

export default function Orders() {
  const { user } = useAuth();
  const { addMany } = useCart();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/orders/me")
      .then(({ data }) => { if (alive) setOrders(data); })
      .catch(() => { if (alive) setOrders([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return ""; }
  };

  const reorder = async (order) => {
    setReordering(order.id);
    try {
      // Refresh stock + active status from the live catalogue before refilling cart.
      const { data: resp } = await api.get("/products", { params: { only_active: false, limit: 100 } });
      const catalog = Array.isArray(resp) ? resp : (resp.items || []);
      const byId = new Map(catalog.map((p) => [p.id, p]));
      const ok = [];
      const skipped = [];
      for (const i of (order.items || [])) {
        const live = byId.get(i.id);
        if (!live || !live.active) { skipped.push(i.name); continue; }
        if ((live.stock ?? 0) < i.qty) { skipped.push(`${i.name} (out of stock)`); continue; }
        ok.push({ id: live.id, name: live.name, image: live.image, weight: live.weight, price: live.price, qty: i.qty });
      }
      if (!ok.length) {
        toast.error("None of these items are available right now.");
        return;
      }
      addMany(ok);
      if (skipped.length) toast.warning(`Skipped: ${skipped.join(", ")}`);
      toast.success(`Added ${ok.length} item${ok.length > 1 ? "s" : ""} to your cart.`);
      nav("/cart");
    } catch {
      toast.error("Reorder failed. Please try again.");
    } finally {
      setReordering(null);
    }
  };

  return (
    <div data-testid="page-orders" className="bg-[var(--bg)] pt-[110px] md:pt-[120px] pb-20 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-4 md:px-10">
        <div className="overline overline-green">Your Account</div>
        <h1 className="font-serif-display text-3xl md:text-5xl mt-3 font-semibold text-[var(--ink)]">My Orders</h1>
        <p className="text-[var(--ink-3)] mt-2">{orders.length} order{orders.length === 1 ? "" : "s"} placed by {user?.name}</p>

        {!orders.length ? (
          <Reveal>
            <div className="card-soft p-14 text-center mt-10">
              <Package size={42} className="text-[var(--gold)] mx-auto mb-4" />
              <p className="font-serif-display text-2xl text-[var(--green)]">No orders yet.</p>
              <p className="text-sm text-[var(--ink-3)] mt-2">When you place an order it will appear here.</p>
              <Link to="/products" data-testid="orders-shop" className="btn-primary mt-6 inline-flex">Shop Now <ArrowRight size={14} /></Link>
            </div>
          </Reveal>
        ) : (
          <div className="mt-8 space-y-5">
            {orders.map((o, idx) => (
              <Reveal key={o.id} delay={(idx % 4) * 0.04}>
                <div data-testid={`order-card-${o.id}`} className="card-soft p-5 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-5 border-b border-[var(--line-soft)]">
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      <Meta icon={Package} label="Order ID" value={o.order_no || o.id} testId={`order-id-${o.id}`} />
                      <Meta icon={Calendar} label="Placed" value={fmtDate(o.created_at)} />
                      <Meta icon={CreditCard} label="Payment" value={(o.method || "card").toUpperCase()} />
                    </div>
                    <div className="text-left md:text-right">
                      <div className="kbd text-[var(--ink-3)] text-[0.6rem]">Total</div>
                      <div className="font-serif-display text-2xl font-semibold text-[var(--green)]">₹{o.total}</div>
                      <div className="flex items-center gap-2 mt-1.5 md:justify-end flex-wrap">
                        <span className="inline-block text-[0.6rem] uppercase tracking-widest font-semibold text-[var(--green)] bg-[var(--bg-cream)] border border-[var(--line-soft)] rounded-full px-3 py-1">
                          {o.status || "Confirmed"}
                        </span>
                        <button
                          type="button"
                          data-testid={`order-reorder-${o.id}`}
                          onClick={() => reorder(o)}
                          disabled={reordering === o.id}
                          className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-widest font-semibold text-[var(--gold)] hover:text-[var(--green)] border border-[var(--gold)] hover:border-[var(--green)] bg-white rounded-full px-3 py-1 transition disabled:opacity-50"
                        >
                          <RotateCcw size={11} className={reordering === o.id ? "animate-spin" : ""} />
                          {reordering === o.id ? "Adding…" : "Reorder"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                    <div className="space-y-3">
                      {(o.items || []).slice(0, 4).map((i) => (
                        <div key={i.id} className="flex gap-3 items-center">
                          <div className="w-12 h-12 rounded-md bg-white border border-[var(--line-soft)] overflow-hidden shrink-0">
                            <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[var(--ink)] truncate">{i.name}</div>
                            <div className="kbd text-[0.55rem] text-[var(--ink-3)] mt-0.5">Qty {i.qty} · {i.weight}</div>
                          </div>
                          <div className="text-sm font-semibold text-[var(--green)]">{i.price}</div>
                        </div>
                      ))}
                      {(o.items || []).length > 4 && (
                        <div className="text-xs text-[var(--ink-3)] kbd">+ {o.items.length - 4} more item{o.items.length - 4 === 1 ? "" : "s"}</div>
                      )}
                    </div>

                    {o.shipping_address && (
                      <div className="bg-[var(--bg-cream)] rounded-md p-4 text-sm">
                        <div className="flex items-center gap-2 kbd text-[var(--ink-3)] text-[0.6rem] mb-2">
                          <MapPin size={11} /> Ship to
                        </div>
                        <div className="font-semibold text-[var(--ink)]">{o.shipping_address.fullName}</div>
                        <div className="text-[var(--ink-2)] leading-relaxed mt-1">
                          {o.shipping_address.address}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.pincode}
                        </div>
                        <div className="text-[var(--ink-3)] text-xs mt-2">{o.shipping_address.email} · {o.shipping_address.phone}</div>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className="text-[var(--gold)] shrink-0" />
      <div>
        <div className="kbd text-[var(--ink-3)] text-[0.55rem]">{label}</div>
        <div data-testid={testId} className="text-sm font-semibold text-[var(--ink)]">{value}</div>
      </div>
    </div>
  );
}
